"use client";

import { usePathname } from "next/navigation";
import { Suspense, type ReactNode } from "react";

import { isStaffPanelPath } from "@/lib/staff-panel-path";

type StaffAwareMainLayoutProps = {
	header: ReactNode;
	footer: ReactNode;
	sidebar: ReactNode;
	productBanners: ReactNode;
	children: ReactNode;
};

function StaffAwareMainLayoutInner({
	header,
	footer,
	sidebar,
	productBanners,
	children,
}: StaffAwareMainLayoutProps) {
	const pathname = usePathname() ?? "";
	const isStaff = isStaffPanelPath(pathname);

	return (
		<div className="main-layout-wrapper flex min-h-screen flex-col">
			{!isStaff ? (
				<>
					{productBanners}
					{header}
				</>
			) : null}
			<div id="main-content" tabIndex={-1} className="flex-1 outline-none">
				{children}
			</div>
			{!isStaff ? (
				<>
					{sidebar}
					{footer}
				</>
			) : null}
		</div>
	);
}

/** Omit product chrome until pathname streams — safe for staff route prerender. */
function StaffAwareMainLayoutFallback({ children }: Pick<StaffAwareMainLayoutProps, "children">) {
	return (
		<div className="main-layout-wrapper flex min-h-screen flex-col">
			<div id="main-content" tabIndex={-1} className="flex-1 outline-none">
				{children}
			</div>
		</div>
	);
}

export function StaffAwareMainLayout(props: StaffAwareMainLayoutProps) {
	return (
		<Suspense
			fallback={<StaffAwareMainLayoutFallback>{props.children}</StaffAwareMainLayoutFallback>}
		>
			<StaffAwareMainLayoutInner {...props} />
		</Suspense>
	);
}
